import {Button, Grid, List, ListItemButton, ListSubheader, Typography} from "@mui/material";
import {Field, Form, Formik, FormikValues} from "formik";
import {AdvancedMarker, Map, Pin, useMap} from "@vis.gl/react-google-maps";
import React, {useEffect, useState} from "react";
import {getLocations} from "../common/api";
import SearchIcon from '@mui/icons-material/Search';
import GooglePlacesAutocomplete from "./components/googlePlacesAutocomplete";
import DirectionsIcon from '@mui/icons-material/Directions';

type Poi = { key: string, location: google.maps.LatLngLiteral }
type ContactAddress = {
    poi?: Poi,
    addressLine?: string,
    addressLine2?: string,
    distance?: number,
    postalCode?: string,
    cityName?: string,
    stateCode?: string,
    countryCode?: string
}

export function LocationsMap() {
    const map = useMap();

    const [currentLocation, setCurrentLocation] =
        useState<google.maps.LatLngLiteral>({lng: 0, lat: 0})
    const [selectedLocation, setSelectedLocation] =
        useState<ContactAddress>()
    const [locations, setLocations] = useState<ContactAddress[]>([])

    const [isFirstSearch, setIsFirstSearch] = useState(true)
    const [isLoading, setIsLoading] = useState(false)

    const [autocompleteValue, setAutocompleteValue] = useState<any>(null);

    const renderPinForLocation = (location: ContactAddress) => {
        return <Pin background={'rgb(62, 128, 255)'} glyphColor={'#fff'} borderColor={'#000'}
                    scale={selectedLocation === location ? 1.4 : 1.2}/>
    }

    const renderPoiMarkers = (locations: ContactAddress[]) => {
        return (
            <>
                {locations.map((location: ContactAddress) => (
                    <AdvancedMarker
                        key={location?.poi?.key}
                        position={location?.poi?.location}
                        onClick={() => handleLocationClick(location)}
                    >
                        {renderPinForLocation(location)}
                    </AdvancedMarker>
                ))}
            </>
        );
    };

    const mapContactAddressToPoi = (contactAddress: any): Poi | null => {
        if (contactAddress.contactAddressId && contactAddress.latitude && contactAddress.longitude)
            return {
                key: contactAddress.contactAddressId,
                location: {lat: contactAddress.latitude, lng: contactAddress.longitude}
            }
        return null
    }

    const getLocationsAsync = async (variables: any) => {
        let isSuccess = false

        const response = await getLocations(variables)
        if (response?.data?.outputs?.contactAddresses) {
            const points = response.data.outputs.contactAddresses.map(
                (contactAddress: any) => {
                    return {
                        ...contactAddress,
                        poi: mapContactAddressToPoi(contactAddress)
                    }
                }
            ).filter((x: any) => x !== null && x.poi !== null)

            isSuccess = true
            setLocations(points)
        }

        return isSuccess
    }

    const getCurrentLocationAsync = async (search: string) => {
        const geocoder = new google.maps.Geocoder()
        const geocoderResponse = await geocoder.geocode({address: search})

        if (geocoderResponse.results) {
            const resultLocation = geocoderResponse.results?.[0]?.geometry.location
            const currentLocation = {
                lat: resultLocation?.lat(), lng: resultLocation?.lng()
            }
            setCurrentLocation(currentLocation);
            return currentLocation

        } else {
            console.log('Geocode was not successful for the following reason: ' + geocoderResponse);
        }
    }

    const onFindClick = async (values: FormikValues) => {
        setLocations([])

        let currentLocation;
        if (autocompleteValue?.geometry?.location)
            currentLocation = autocompleteValue.geometry.location
        else
            currentLocation = await getCurrentLocationAsync(values?.search)


        let isLocationsWfSuccess = false
        if (currentLocation)
            isLocationsWfSuccess = await getLocationsAsync({currentLocation})

        if (isLocationsWfSuccess)
            setCurrentLocation(currentLocation)

        if (isFirstSearch && isLocationsWfSuccess)
            setIsFirstSearch(false)
    }

    const handleLocationClick = (location: ContactAddress) => {
        if (map && location.poi?.location) {
            setSelectedLocation(location)
            map.panTo(location.poi.location)
        }
    }

    useEffect(() => {
        map?.panTo(currentLocation)
    }, [currentLocation]);

    return <Grid>
        <Formik initialValues={{search: ""}} onSubmit={onFindClick}>
            {({values, setFieldValue}) => (
                <Form>
                    {isFirstSearch ?
                        <Grid className={"locations-search"}>
                            <Field name='search'
                                   render={() =>
                                       <GooglePlacesAutocomplete
                                           value={autocompleteValue}
                                           setValue={setAutocompleteValue}
                                           fieldName={'search'}
                                           minAutocompleteLength={2}
                                       />
                                   }>
                            </Field>
                            <Button type='submit'><SearchIcon/></Button>
                        </Grid>
                        : null
                    }
                    <Grid display={"flex"} visibility={!isFirstSearch ? "visible" : "hidden"}
                          className={"locations-result"}>
                        <Grid className={'locations-menu'}>
                            <Grid className={"locations-search"}>
                                <Field name='search'
                                       render={() =>
                                           <GooglePlacesAutocomplete
                                               value={autocompleteValue}
                                               setValue={setAutocompleteValue}
                                               fieldName={'search'}
                                               minAutocompleteLength={2}
                                           />
                                       }>
                                </Field>
                                <Button type='submit'><SearchIcon/></Button>
                            </Grid>
                            <List>
                                <ListSubheader>{`${locations.length && locations.length !== 0 ? locations.length : "No"} locations near found`}</ListSubheader>
                                {locations.map((location, index) =>
                                    <ListItemButton key={`${location.poi?.key}-${index}`}
                                                    onClick={() => handleLocationClick(location)}
                                                    selected={selectedLocation === location}>
                                        <Grid container>
                                            {/*<Grid xs={2}>
                                                <div>
                                                    <AdvancedMarker
                                                        position={location.poi?.location}>
                                                        {renderPinForLocation(location)}
                                                    </AdvancedMarker>
                                                </div>
                                            </Grid>*/}
                                            <Grid
                                                xs={9}>
                                                <Typography>{location.addressLine}</Typography>
                                                <Typography>{location.addressLine2}</Typography>
                                                <Typography>{[[location.cityName, location.stateCode].join(", "), location.postalCode].join(" ")}</Typography>
                                            </Grid>
                                            <Grid xs={3}
                                                  display={'flex'}
                                                  flexDirection={'column'}
                                                  alignItems={'flex-end'}
                                                  textAlign={"end"}>
                                                <Grid>{(location.distance ?? 0).toFixed(2)} mi</Grid>
                                            </Grid>
                                            <Grid xs={12} display={'flex'} justifyContent={'flex-end'}>
                                                <Button
                                                    href={`https://www.google.com/maps/search/?api=1&query=${location.poi?.location?.lat}%2C${location.poi?.location?.lng}`}
                                                    target={'_blank'}
                                                >
                                                    <DirectionsIcon sx={{mr: 1}}/>
                                                    Get directions
                                                </Button>
                                            </Grid>
                                        </Grid>

                                    </ListItemButton>
                                )}
                            </List>
                        </Grid>

                        <Grid className={'google-map'} width={"100%"} height={'100%'}>
                            <Map
                                style={{width: '100%', height: '100%'}}
                                defaultCenter={currentLocation}
                                defaultZoom={12}
                                gestureHandling={'greedy'}
                                disableDefaultUI={true}
                                mapId={"DEMO_MAP_ID"}
                            >
                                {renderPoiMarkers(locations)}
                            </Map>
                        </Grid>

                    </Grid>
                </Form>
            )}
        </Formik>
    </Grid>
}